import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, Tag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { productById, fmt, bezPvn } from "../data/store";

export default function CartPage() {
  const { items, setQty, remove, discount, setDiscount } = useCart();
  const navigate = useNavigate();
  const [promo, setPromo] = useState("");

  const lines = items.map((i) => ({ ...i, p: productById(i.id) })).filter((l) => l.p);
  const subtotal = lines.reduce((s, l) => s + l.p.price * l.qty, 0);
  const shipping = subtotal >= 60 || subtotal === 0 ? 0 : 4.99;
  const total = subtotal * (1 - discount) + shipping;

  const applyPromo = (e) => {
    e.preventDefault();
    if (promo.trim().toUpperCase() === "DEMO10") {
      setDiscount(0.1);
      toast.success("Atlaides kods piemērots: –10%");
    } else {
      toast.error("Nederīgs atlaides kods");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 mt-6">
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight uppercase mb-6" data-testid="cart-page-title">Iepirkumu grozs</h1>
      {lines.length === 0 ? (
        <div className="text-center py-16" data-testid="cart-empty-state">
          <p className="text-lg font-bold">Grozs ir tukšs</p>
          <Link to="/" className="inline-block mt-4 bg-[#111111] hover:bg-[#3A3A3A] text-white font-semibold px-6 py-3 rounded-md transition-colors">Turpināt iepirkties</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {lines.map((l) => (
              <div key={l.id} className="flex gap-4 bg-white border border-[#E2E2E2] rounded-md p-4" data-testid={`cart-item-${l.id}`}>
                <Link to={`/prece/${l.id}`}><img src={l.p.image} alt={l.p.name} className="w-20 h-20 object-cover rounded bg-[#F5F5F5]" /></Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/prece/${l.id}`} className="text-sm font-semibold hover:text-[#111111] line-clamp-2">{l.p.name}</Link>
                  <div className="text-xs text-[#6B6B6B] mt-0.5">{l.p.brand} · SKU: {l.p.sku}{l.p.packQty > 1 && ` · Iep. ${l.p.packQty} gab.`}</div>
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    <div className="flex items-center border border-[#E2E2E2] rounded-md">
                      <button onClick={() => setQty(l.id, l.qty - 1)} className="p-2 hover:bg-[#F5F5F5]" data-testid={`cart-qty-minus-${l.id}`}><Minus size={13} /></button>
                      <span className="w-8 text-center text-sm font-bold">{l.qty}</span>
                      <button onClick={() => setQty(l.id, l.qty + 1)} className="p-2 hover:bg-[#F5F5F5]" data-testid={`cart-qty-plus-${l.id}`}><Plus size={13} /></button>
                    </div>
                    <span className="text-xs text-[#6B6B6B]">{fmt(l.p.price)}/gab.</span>
                    <span className="font-bold ml-auto">{fmt(l.p.price * l.qty)}</span>
                    <button onClick={() => remove(l.id)} className="text-[#6B6B6B] hover:text-[#D0121A]" data-testid={`cart-remove-${l.id}`} aria-label="Dzēst"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
            <Link to="/" className="inline-block text-sm font-semibold text-[#111111] hover:underline mt-2">← Turpināt iepirkties</Link>
          </div>

          <div className="bg-[#F5F5F5] rounded-md p-6 h-fit" data-testid="cart-summary">
            <h2 className="text-lg font-bold mb-4">Pasūtījuma kopsavilkums</h2>
            <form onSubmit={applyPromo} className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Tag size={14} className="absolute left-3 top-3 text-[#6B6B6B]" />
                <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Atlaides kods" data-testid="promo-input"
                  className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" />
              </div>
              <button type="submit" data-testid="promo-apply-btn" className="bg-[#111111] hover:bg-[#3A3A3A] text-white text-sm font-semibold px-4 rounded-md transition-colors">Piemērot</button>
            </form>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#6B6B6B]">Starpsumma (ar PVN)</span><span className="font-semibold">{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#6B6B6B]">t.sk. PVN 21%</span><span>{fmt(subtotal - bezPvn(subtotal))}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#D0121A]"><span>Atlaide –10%</span><span>–{fmt(subtotal * discount)}</span></div>}
              <div className="flex justify-between"><span className="text-[#6B6B6B]">Piegāde</span><span className="font-semibold">{shipping === 0 ? "Bezmaksas" : fmt(shipping)}</span></div>
              {subtotal < 60 && <p className="text-xs text-[#6B6B6B]">Vēl {fmt(60 - subtotal)} līdz bezmaksas piegādei</p>}
              <div className="flex justify-between text-lg font-extrabold border-t border-[#E2E2E2] pt-3 mt-3"><span>Kopā</span><span data-testid="cart-total">{fmt(total)}</span></div>
            </div>
            <button onClick={() => navigate("/noformesana")} data-testid="cart-checkout-btn"
              className="w-full bg-[#D0121A] hover:bg-[#A80D14] text-white font-bold py-3.5 rounded-md mt-5 flex items-center justify-center gap-2 transition-colors">
              Uz apmaksu <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
